import React, { useState, useRef, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  Box, Stack, ToggleButton, ToggleButtonGroup, Button, TextField,
  IconButton, Tooltip, Paper, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { uploadFile } from '../api/endpoints/media';
import UploadAdapterPlugin from '../utils/CKEditorUploadAdapter';

const editorConfig = {
  toolbar: [
    'heading', '|',
    'bold', 'italic', '|',
    'link', 'blockQuote', '|',
    'numberedList', 'bulletedList', 'outdent', 'indent', '|',
    'uploadImage', 'mediaEmbed', 'insertTable', '|',
    'undo', 'redo',
  ],
  extraPlugins: [UploadAdapterPlugin],
  image: {
    toolbar: ['imageTextAlternative', 'imageStyle:inline', 'imageStyle:block', 'imageStyle:side'],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
  },
  placeholder: 'Start writing...',
};

export default function RichTextEditor({ value, onChange, height }) {
  const [mode, setMode] = useState('visual');
  const textareaRef = useRef(null);
  const editorRef = useRef(null);

  const handleModeChange = (_, newMode) => {
    if (newMode) setMode(newMode);
  };

  const insertHtml = (html) => {
    if (mode === 'visual' && editorRef.current) {
      const editor = editorRef.current;
      editor.model.change((writer) => {
        const viewFragment = editor.data.processor.toView(html);
        const modelFragment = editor.data.toModel(viewFragment);
        editor.model.insertContent(modelFragment);
      });
    } else if (mode === 'code' && textareaRef.current) {
      const ta = textareaRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const before = (value || '').substring(0, start);
      const after = (value || '').substring(end);
      onChange(before + html + after);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + html.length;
        ta.focus();
      });
    }
  };

  const handleImportHtml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    if (mode === 'visual' && editorRef.current) {
      editorRef.current.setData(text);
    } else {
      onChange(text);
    }
    toast.success('HTML content imported');
    e.target.value = '';
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadFile(file);
      const url = res.data.data?.url || '';
      const imgHtml = `<figure class="image"><img src="${url}" alt="${file.name}"></figure><p>&nbsp;</p>`;
      insertHtml(imgHtml);
      toast.success('Image uploaded & inserted');
    } catch { toast.error('Image upload failed'); }
    e.target.value = '';
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadFile(file);
      const url = res.data.data?.url || '';
      const linkHtml = `<p><a href="${url}" target="_blank">${file.name}</a></p>`;
      insertHtml(linkHtml);
      toast.success('File uploaded & link inserted');
    } catch { toast.error('File upload failed'); }
    e.target.value = '';
  };

  const previewDoc = `<!DOCTYPE html><html><head>
<style>
  body { font-family: system-ui, sans-serif; line-height: 1.7; padding: 24px; max-width: 800px; margin: 0 auto; }
  img { max-width: 100%; height: auto; }
  table { border-collapse: collapse; width: 100%; }
  td, th { border: 1px solid #ccc; padding: 8px; }
  pre { background: #f5f5f5; padding: 12px; border-radius: 6px; overflow-x: auto; }
  blockquote { border-left: 4px solid #ddd; margin-left: 0; padding-left: 16px; color: #666; }
</style></head><body>${value || ''}</body></html>`;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
        <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
          <ToggleButton value="visual"><EditIcon fontSize="small" sx={{ mr: 0.5 }} />Visual</ToggleButton>
          <ToggleButton value="code"><CodeIcon fontSize="small" sx={{ mr: 0.5 }} />Code</ToggleButton>
          <ToggleButton value="preview"><VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />Preview</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" component="label" startIcon={<DescriptionIcon />}>
          Import HTML
          <input type="file" hidden accept=".html,.htm" onChange={handleImportHtml} />
        </Button>
        <Button size="small" variant="outlined" component="label" startIcon={<ImageIcon />}>
          Insert Image
          <input type="file" hidden accept="image/*" onChange={handleUploadImage} />
        </Button>
        <Button size="small" variant="outlined" component="label" startIcon={<AttachFileIcon />}>
          Attach File
          <input type="file" hidden onChange={handleUploadFile} />
        </Button>
      </Stack>

      {mode === 'visual' && (
        <Box sx={{
          border: 1, borderColor: 'divider', borderRadius: 1,
          '& .ck-editor__editable': { minHeight: height || 400 },
        }}>
          <CKEditor
            editor={ClassicEditor}
            data={value || ''}
            config={editorConfig}
            onChange={(_, editor) => onChange(editor.getData())}
            onReady={(editor) => { editorRef.current = editor; }}
          />
        </Box>
      )}

      {mode === 'code' && (
        <TextField
          inputRef={textareaRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          multiline
          rows={18}
          sx={{
            '& textarea': {
              fontFamily: 'Consolas, Monaco, monospace',
              fontSize: 13,
              lineHeight: 1.5,
              minHeight: height || 400,
            },
          }}
          placeholder="<h1>Hello World</h1>&#10;<p>Write your HTML content here...</p>"
        />
      )}

      {mode === 'preview' && (
        <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
          <iframe
            title="Content Preview"
            srcDoc={previewDoc}
            sandbox=""
            style={{
              width: '100%',
              height: height || 400,
              border: 'none',
              display: 'block',
            }}
          />
        </Paper>
      )}
    </Box>
  );
}
